import React, { FC, Fragment, useEffect, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Button, Menu, MenuItem, Typography } from '@mui/material';
import { Action } from 'models/action';
import { ActionSet } from 'models/action-set';

type ManageActionsProps = {
    actionSet: ActionSet;
    actions: Action[];
    subCategoryId: string;
    onActionsChanged?: (actions: Action[]) => void;
};
const ManageActions: FC<ManageActionsProps> = ({ actionSet, actions, subCategoryId, onActionsChanged }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedActions, setSelectedActions] = useState<Action[]>([]);

    useEffect(() => {
        setSelectedActions(actions);
    }, [actions]);

    const handleDeleteAction = (index: number) => {
        setSelectedActions((prev) => {
            const updated = prev.filter((_, i) => i !== index);
            if (onActionsChanged) {
                onActionsChanged(updated);
            }
            return updated;
        });
    };

    const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAddAction = (actionId: string) => {
        const actionToAdd = actionSet.actions.find((a) => a._id?.toString() === actionId);
        if (actionToAdd && !selectedActions.some((a) => a.number === actionToAdd.number)) {
            setSelectedActions((prev) => {
                const updated = [...prev, actionToAdd];
                if (onActionsChanged) {
                    onActionsChanged(updated);
                }
                return updated;
            });
        }
        handleMenuClose();
    };

    const renderMenuItems = () => {
        const availableActions = actionSet.actions.filter(
            (a) =>
                !selectedActions.some((sa) => sa.number === a.number) &&
                Array.isArray(a.subCategory) &&
                a.subCategory.some((subCat) => subCat._id === subCategoryId),
        );

        return availableActions.map((action) => (
            <MenuItem key={action.number} onClick={() => handleAddAction(action._id || '')}>
                {`${action.number} - ${action.name} - ${action?.mainCategory?.name || ''} / ${action?.subCategory[0]?.name || ''}  `}
            </MenuItem>
        ));
    };

    return (
        <>
            <Box display="flex" gap={2} alignItems="center" flexWrap="nowrap">
                <Box flexGrow={1} minWidth={0}>
                    {selectedActions.map((action, index) => (
                        <Fragment key={index}>
                            <Chip
                                label={
                                    <span>
                                        {`${action.number} - ${action.name} `}
                                        {action.quotable && (
                                            <Typography
                                                component="strong"
                                                sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: 'inherit' }}
                                            >
                                                {`(${translate('common:QUOTABLE')})`}
                                            </Typography>
                                        )}
                                    </span>
                                }
                                onDelete={index !== 0 ? () => handleDeleteAction(index) : undefined}
                                sx={{ marginRight: 1 }}
                            />
                        </Fragment>
                    ))}
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                    <Button
                        variant="contained"
                        onClick={handleMenuOpen}
                        disabled={
                            actionSet.actions.filter((a) => !selectedActions.some((sa) => sa.number === a.number))
                                .length === 0
                        }
                    >
                        {translate('orders:ADD_ACTION')}
                    </Button>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                        {renderMenuItems()}
                    </Menu>
                </Box>
            </Box>
        </>
    );
};

export default ManageActions;
